/**
 * Pluggable metrics flush targets (logger, OTLP HTTP JSON, Sentry breadcrumb).
 * No prom-client; Prometheus scrape remains GET /api/system/metrics.
 */

import { logger } from '../shared/logger';
import { computeRedSummary } from './red-summary';
import { getMetricsSnapshot } from './metrics';

export type MetricsExporter = {
  readonly name: string;
  flush(summary: ReturnType<typeof computeRedSummary>): void | Promise<void>;
};

const exporters: MetricsExporter[] = [];

export function registerMetricsExporter(exporter: MetricsExporter): void {
  if (exporters.some((e) => e.name === exporter.name)) return;
  exporters.push(exporter);
}

export function resetMetricsExportersForTests(): void {
  exporters.length = 0;
}

export function listMetricsExporters(): readonly string[] {
  return exporters.map((e) => e.name);
}

function handleExporterError(exporterName: string, error: unknown): void {
  const err = error instanceof Error ? error : new Error(String(error));
  logger.warn(`Observability: metrics exporter "${exporterName}" failed`, {
    error: err.message,
  });
}

export async function flushMetricsAsync(): Promise<void> {
  if (exporters.length === 0) return;

  const summary = computeRedSummary();
  await Promise.all(
    exporters.map(async (exporter) => {
      try {
        await exporter.flush(summary);
      } catch (error) {
        handleExporterError(exporter.name, error);
      }
    })
  );
}

export function flushMetrics(): void {
  void flushMetricsAsync();
}

export function createLoggerMetricsExporter(): MetricsExporter {
  return {
    name: 'logger',
    flush(summary) {
      logger.info('RED metrics flush', {
        window: summary.window,
        totalRequests: summary.totalRequests,
        requests5xx: summary.requests5xx,
        errorRate5xx: Number(summary.errorRate5xx.toFixed(4)),
        approximateP95Seconds: Number(summary.approximateP95Seconds.toFixed(3)),
      });
    },
  };
}

export function createOtlpHttpMetricsExporter(endpoint: string): MetricsExporter {
  const url = endpoint.replace(/\/$/, '');
  return {
    name: 'otlp-http',
    async flush(summary) {
      const body = {
        resourceMetrics: [
          {
            scopeMetrics: [
              {
                metrics: [
                  {
                    name: 'http.requests.total',
                    sum: { dataPoints: [{ asInt: summary.totalRequests }] },
                  },
                  {
                    name: 'http.requests.5xx_rate',
                    gauge: { dataPoints: [{ value: summary.errorRate5xx }] },
                  },
                  {
                    name: 'http.request.duration.p95_seconds',
                    gauge: {
                      dataPoints: [{ value: summary.approximateP95Seconds }],
                    },
                  },
                ],
              },
            ],
          },
        ],
        inflomatrix: {
          expositionHint: 'Use GET /api/system/metrics for Prometheus histograms',
          snapshotBytes: getMetricsSnapshot().length,
        },
      };

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      const auth = process.env.METRICS_OTLP_HTTP_HEADERS?.trim();
      if (auth) {
        const [key, ...rest] = auth.split(':');
        if (key && rest.length > 0) {
          headers[key] = rest.join(':');
        }
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(5_000),
      });

      if (!response.ok) {
        throw new Error(`OTLP HTTP ${response.status}: ${response.statusText}`);
      }
    },
  };
}

export function createSentryBreadcrumbExporter(): MetricsExporter {
  return {
    name: 'sentry-breadcrumb',
    async flush(summary) {
      if (typeof window === 'undefined') return;
      const { isSentryEnabled } = await import('./init');
      if (!isSentryEnabled()) return;
      const Sentry = await import('@sentry/react');
      Sentry.addBreadcrumb({
        category: 'metrics.red',
        message: 'RED metrics flush',
        level: summary.errorRate5xx > 0.05 ? 'warning' : 'info',
        data: {
          totalRequests: summary.totalRequests,
          requests5xx: summary.requests5xx,
          errorRate5xx: summary.errorRate5xx,
          approximateP95Seconds: summary.approximateP95Seconds,
        },
      });
    },
  };
}
